/**
 * Instalação do validador como app (PWA).
 *
 * Três situações, e cada uma pede um convite diferente:
 *
 * - Chrome e Edge (Android e computador) avisam que o site pode ser instalado
 *   (`beforeinstallprompt`). O evento é guardado, e o botão "Instalar" da tela
 *   abre a instalação nativa com um toque.
 * - O iPhone não tem esse aviso nem botão possível: nenhum site instala sozinho
 *   no Safari. Só dá para ensinar o caminho — Compartilhar, Adicionar à Tela
 *   de Início.
 * - Já instalado (abrindo pelo ícone), não há o que oferecer.
 *
 * O evento é capturado na carga do módulo, antes da primeira tela: o Chrome o
 * dispara uma vez, cedo, e quem não estiver ouvindo perde.
 */

import {useEffect, useState} from 'react';

interface ConviteDoNavegador extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{outcome: 'accepted' | 'dismissed'}>;
}

export type Plataforma = 'iphone' | 'android' | 'computador';

let convite: ConviteDoNavegador | null = null;
let instaladoNestaVisita = false;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach(ouvinte => ouvinte());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', evento => {
    // Sem isto o Chrome mostra a barrinha dele por conta própria, por cima da
    // tela que estiver aberta — inclusive do validador, com a fila andando. O
    // convite passa a ser nosso, e só no login.
    evento.preventDefault();
    convite = evento as ConviteDoNavegador;
    avisar();
  });
  window.addEventListener('appinstalled', () => {
    convite = null;
    instaladoNestaVisita = true;
    avisar();
  });
}

/** Aberto pelo ícone da tela inicial, e não numa aba do navegador. */
export function rodandoComoApp(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: fullscreen)').matches ||
    (navigator as {standalone?: boolean}).standalone === true
  );
}

export function plataforma(): Plataforma {
  const ua = navigator.userAgent;
  // O iPad se apresenta como Mac; o que o entrega é a tela de toque.
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) {
    return 'iphone';
  }
  return /Android/.test(ua) ? 'android' : 'computador';
}

/**
 * Abre a instalação nativa. O convite do navegador vale uma vez só: recusado,
 * ele só volta depois de recarregar a página — até lá a tela ensina o caminho
 * pelo menu.
 */
async function instalar(): Promise<boolean> {
  const atual = convite;
  if (!atual) {
    return false;
  }
  convite = null;
  avisar();
  await atual.prompt();
  const {outcome} = await atual.userChoice;
  return outcome === 'accepted';
}

export function useInstalacao() {
  const [, setVersao] = useState(0);

  useEffect(() => {
    const ouvinte = () => setVersao(v => v + 1);
    ouvintes.add(ouvinte);
    return () => {
      ouvintes.delete(ouvinte);
    };
  }, []);

  return {
    instalado: instaladoNestaVisita || rodandoComoApp(),
    plataforma: plataforma(),
    /** O navegador oferece instalar com um toque. */
    instalaComUmToque: convite !== null,
    instalar,
  };
}
